import { useEffect, useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { api, formatApiError } from "../../lib/api";
import {
  PARTNER_CATEGORIES, PARTNER_SUBCATEGORIES, PARTNER_STATUSES, PARTNER_PRIORITIES,
  PARTNER_INTERACTION_TYPES, PARTNER_ACTION_TYPES, PARTNER_LANGS, PARTNER_POTENTIAL,
  PARTNER_FREQ, pl, statusEntry, interactionEntry, actionEntry,
} from "../../lib/partnerConstants";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Label } from "../ui/label";
import { Switch } from "../ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../ui/select";
import PartnerMeetingDialog from "./PartnerMeetingDialog";
import { Handshake, Save, Plus, Star, Phone, Mail, MapPin, Clock, CheckCircle2, AlertTriangle, User, CalendarCheck, Pencil, Trash2 } from "lucide-react";

const Field = ({ label, children }) => (
  <div className="space-y-1.5"><Label className="text-xs">{label}</Label>{children}</div>
);
const SectionTitle = ({ children }) => (
  <div className="text-xs uppercase tracking-wider text-emerald-400 font-semibold pt-2">{children}</div>
);

export default function PartnerDetailDialog({ partnerId, open, onClose, onChanged, relationEditable, readOnly }) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language || "fr";
  const [p, setP] = useState(null);
  const [rel, setRel] = useState({ priority: "B", status: "a_identifier", responsible: "", notes: "" });
  const [interactions, setInteractions] = useState([]);
  const [meetings, setMeetings] = useState([]);
  const [meetingOpen, setMeetingOpen] = useState(false);
  const [editMeeting, setEditMeeting] = useState(null);
  const [showAdd, setShowAdd] = useState(false);
  const emptyInteraction = {
    date: new Date().toISOString().slice(0, 16), type: "appel_sortant", note: "", result: "", documents: "",
    next_mode: "action", action_type: "rappeler", action_responsible: "", relance_date: "", no_action_reason: "",
  };
  const [ni, setNi] = useState(emptyInteraction);

  const load = useCallback(async () => {
    if (!partnerId) return;
    try {
      const r = await api.get(`/crm/partners/${partnerId}`);
      const d = r.data;
      setP(d);
      setRel({
        priority: d.relation?.priority || "B", status: d.relation?.status || "a_identifier",
        responsible: d.relation?.responsible || "", notes: d.relation?.notes || "",
      });
      setInteractions(d.interactions || []);
      try { const m = await api.get(`/crm/partners/${partnerId}/meetings`); setMeetings(m.data || []); } catch { /* noop */ }
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  }, [partnerId]);

  useEffect(() => { if (open) { setShowAdd(false); setNi(emptyInteraction); load(); } }, [open, load]); // eslint-disable-line

  if (!p) return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-3xl"><DialogHeader><DialogTitle>{t("crm.partner.detailTitle")}</DialogTitle><DialogDescription className="sr-only">…</DialogDescription></DialogHeader><div className="py-10 text-center text-muted-foreground">…</div></DialogContent>
    </Dialog>
  );

  const setField = (k, v) => setP({ ...p, [k]: v });
  const subcats = PARTNER_SUBCATEGORIES[p.category] || [];

  const saveFiche = async () => {
    try {
      const body = {
        name: p.name, category: p.category, subcategory: p.subcategory || "", contact_name: p.contact_name || "",
        function: p.function || "", phone: p.phone || "", email: p.email || "", address: p.address || "",
        commune: p.commune || "", language: p.language || "fr", potential: p.potential || "",
        zone_covered: p.zone_covered || "", public_served: p.public_served || "",
        orientation_frequency: p.orientation_frequency || "", influence: p.influence || "", needs: p.needs || "",
        current_relation: p.current_relation || "", strategic: !!p.strategic, notes: p.notes || "", archived: !!p.archived,
      };
      await api.put(`/crm/partners/${partnerId}`, body);
      toast.success(t("crm.partner.saved")); onChanged?.();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const saveRelation = async () => {
    try { await api.put(`/crm/partners/${partnerId}/relation`, rel); toast.success(t("crm.partner.relationSaved")); onChanged?.(); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const addInteraction = async () => {
    if (ni.next_mode === "action" && (!ni.action_type || !ni.relance_date)) return toast.error(t("crm.partner.actionType") + " / " + t("crm.partner.relanceDate"));
    if (ni.next_mode === "none" && !ni.no_action_reason.trim()) return toast.error(t("crm.partner.noActionReason"));
    try {
      await api.post(`/crm/partners/${partnerId}/interactions`, {
        ...ni, date: new Date(ni.date).toISOString(),
        documents: ni.documents ? ni.documents.split(",").map((x) => x.trim()).filter(Boolean) : [],
      });
      toast.success(t("crm.partner.interactionSaved")); setShowAdd(false); setNi(emptyInteraction); onChanged?.(); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const markDone = async () => {
    try { await api.post(`/crm/partners/${partnerId}/relation/complete`); toast.success(t("crm.partner.markDone")); onChanged?.(); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const delMeeting = async (m) => {
    if (!window.confirm(t("crm.meeting.confirmDelete"))) return;
    try { await api.delete(`/crm/partners/${partnerId}/meetings/${m.id}`); toast.success(t("crm.partner.deleted")); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const reportOk = (m) => (m.rep_result || "").trim() && ((m.rep_next_action || "").trim() || (m.rep_comment || "").trim());
  const MTG_COLOR = { prepare: "#94a3b8", planifie: "#8b5cf6", realise: "#f59e0b", cloture: "#10b981" };

  const relDate = p.relation?.relance_date;
  const overdue = relDate && relDate <= new Date().toISOString().slice(0, 10) && !p.relation?.next_action_done;

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="partner-detail-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 flex-wrap">
            <Handshake className="h-5 w-5 text-emerald-400" /> {p.name}
            {p.strategic && <span className="inline-flex items-center gap-1 text-[11px] font-semibold rounded-full px-2 py-0.5" style={{ color: "#fbbf24", background: "rgba(245,158,11,0.15)" }}><Star className="h-3 w-3" /> {t("crm.partner.strategicBadge")}</span>}
          </DialogTitle>
          <DialogDescription className="text-xs">{pl(lang, PARTNER_CATEGORIES[p.category])}{p.subcategory ? " · " + (subcats.find((s) => s.k === p.subcategory) ? pl(lang, subcats.find((s) => s.k === p.subcategory)) : p.subcategory) : ""}</DialogDescription>
        </DialogHeader>

        {/* Compteurs + relance */}
        <div className="grid grid-cols-3 gap-2 text-center">
          {[["orientations", p.counts?.orientations], ["requests", p.counts?.requests], ["admissions", p.counts?.admissions]].map(([k, v]) => (
            <div key={k} className="rounded-xl border border-white/10 bg-white/[0.02] py-2" data-testid={`partner-count-${k}`}>
              <div className="text-lg font-bold">{v ?? 0}</div><div className="text-[10px] text-muted-foreground uppercase tracking-wide">{t(`crm.partner.${k}`)}</div>
            </div>
          ))}
        </div>
        {relDate && !p.relation?.next_action_done && (
          <div className={`flex items-center justify-between gap-2 rounded-xl px-3 py-2 text-sm ${overdue ? "bg-rose-500/15 border border-rose-500/30 text-rose-200" : "bg-amber-500/10 border border-amber-500/25 text-amber-200"}`} data-testid="partner-relance-banner">
            <span className="flex items-center gap-2">{overdue ? <AlertTriangle className="h-4 w-4" /> : <Clock className="h-4 w-4" />} {t("crm.partner.nextAction")}: {(actionEntry(p.relation?.next_action) ? pl(lang, actionEntry(p.relation?.next_action)) : p.relation?.next_action)} — {relDate}</span>
            {!readOnly && relationEditable && <Button size="sm" variant="outline" className="h-7" onClick={markDone} data-testid="partner-relance-done"><CheckCircle2 className="h-3.5 w-3.5 mr-1" /> {t("crm.partner.markDone")}</Button>}
          </div>
        )}

        {/* IDENTITÉ */}
        <SectionTitle>{t("crm.partner.sectionIdentity")}</SectionTitle>
        <div className="grid sm:grid-cols-2 gap-3">
          <div className="sm:col-span-2"><Field label={t("crm.partner.organisation")}><Input value={p.name || ""} onChange={(e) => setField("name", e.target.value)} disabled={readOnly} data-testid="partner-f-name" /></Field></div>
          <Field label={t("crm.partner.category")}>
            <Select value={p.category} onValueChange={(v) => setP({ ...p, category: v, subcategory: "" })} disabled={readOnly}>
              <SelectTrigger data-testid="partner-f-category"><SelectValue /></SelectTrigger>
              <SelectContent>{Object.keys(PARTNER_CATEGORIES).map((k) => <SelectItem key={k} value={k}>{pl(lang, PARTNER_CATEGORIES[k])}</SelectItem>)}</SelectContent>
            </Select>
          </Field>
          <Field label={t("crm.partner.subcategory")}>
            <Select value={p.subcategory || ""} onValueChange={(v) => setField("subcategory", v)} disabled={readOnly}>
              <SelectTrigger data-testid="partner-f-subcategory"><SelectValue placeholder="—" /></SelectTrigger>
              <SelectContent>{subcats.map((s) => <SelectItem key={s.k} value={s.k}>{pl(lang, s)}</SelectItem>)}</SelectContent>
            </Select>
          </Field>
          <Field label={t("crm.partner.contactName")}><Input value={p.contact_name || ""} onChange={(e) => setField("contact_name", e.target.value)} disabled={readOnly} data-testid="partner-f-contact" /></Field>
          <Field label={t("crm.partner.function")}><Input value={p.function || ""} onChange={(e) => setField("function", e.target.value)} disabled={readOnly} /></Field>
          <Field label={t("crm.partner.phone")}><Input value={p.phone || ""} onChange={(e) => setField("phone", e.target.value)} disabled={readOnly} data-testid="partner-f-phone" /></Field>
          <Field label={t("crm.partner.email")}><Input value={p.email || ""} onChange={(e) => setField("email", e.target.value)} disabled={readOnly} data-testid="partner-f-email" /></Field>
          <Field label={t("crm.partner.address")}><Input value={p.address || ""} onChange={(e) => setField("address", e.target.value)} disabled={readOnly} /></Field>
          <Field label={t("crm.partner.commune")}><Input value={p.commune || ""} onChange={(e) => setField("commune", e.target.value)} disabled={readOnly} data-testid="partner-f-commune" /></Field>
          <Field label={t("crm.partner.language")}>
            <Select value={p.language || "fr"} onValueChange={(v) => setField("language", v)} disabled={readOnly}>
              <SelectTrigger data-testid="partner-f-language"><SelectValue /></SelectTrigger>
              <SelectContent>{Object.keys(PARTNER_LANGS).map((k) => <SelectItem key={k} value={k}>{PARTNER_LANGS[k]}</SelectItem>)}</SelectContent>
            </Select>
          </Field>
        </div>

        {/* QUALIFICATION */}
        <SectionTitle>{t("crm.partner.sectionQualification")}</SectionTitle>
        <div className="grid sm:grid-cols-2 gap-3">
          <Field label={t("crm.partner.potential")}>
            <Select value={p.potential || ""} onValueChange={(v) => setField("potential", v)} disabled={readOnly}>
              <SelectTrigger><SelectValue placeholder="—" /></SelectTrigger>
              <SelectContent>{Object.keys(PARTNER_POTENTIAL).map((k) => <SelectItem key={k} value={k}>{pl(lang, PARTNER_POTENTIAL[k])}</SelectItem>)}</SelectContent>
            </Select>
          </Field>
          <Field label={t("crm.partner.orientationFrequency")}>
            <Select value={p.orientation_frequency || ""} onValueChange={(v) => setField("orientation_frequency", v)} disabled={readOnly}>
              <SelectTrigger><SelectValue placeholder="—" /></SelectTrigger>
              <SelectContent>{Object.keys(PARTNER_FREQ).map((k) => <SelectItem key={k} value={k}>{pl(lang, PARTNER_FREQ[k])}</SelectItem>)}</SelectContent>
            </Select>
          </Field>
          <Field label={t("crm.partner.zoneCovered")}><Input value={p.zone_covered || ""} onChange={(e) => setField("zone_covered", e.target.value)} disabled={readOnly} /></Field>
          <Field label={t("crm.partner.publicServed")}><Input value={p.public_served || ""} onChange={(e) => setField("public_served", e.target.value)} disabled={readOnly} /></Field>
          <Field label={t("crm.partner.influence")}><Input value={p.influence || ""} onChange={(e) => setField("influence", e.target.value)} disabled={readOnly} /></Field>
          <Field label={t("crm.partner.currentRelation")}><Input value={p.current_relation || ""} onChange={(e) => setField("current_relation", e.target.value)} disabled={readOnly} /></Field>
          <div className="sm:col-span-2"><Field label={t("crm.partner.needs")}><Textarea rows={2} value={p.needs || ""} onChange={(e) => setField("needs", e.target.value)} disabled={readOnly} /></Field></div>
          <div className="sm:col-span-2"><Field label={t("crm.partner.groupNotes")}><Textarea rows={2} value={p.notes || ""} onChange={(e) => setField("notes", e.target.value)} disabled={readOnly} /></Field></div>
          <label className="flex items-center gap-2 cursor-pointer"><Switch checked={!!p.strategic} onCheckedChange={(v) => setField("strategic", v)} disabled={readOnly} data-testid="partner-f-strategic" /><span className="text-sm">{t("crm.partner.strategic")}</span></label>
        </div>
        {!readOnly && <Button onClick={saveFiche} className="bg-emerald-600 hover:bg-emerald-500 w-full sm:w-auto" data-testid="partner-save-fiche"><Save className="h-4 w-4 mr-2" /> {t("crm.actions.save")} — {t("crm.partner.sectionIdentity")} / {t("crm.partner.sectionQualification")}</Button>}

        {/* RELATION LOCALE */}
        <SectionTitle>{t("crm.partner.sectionRelation")}</SectionTitle>
        {!relationEditable ? (
          <p className="text-sm text-amber-300/80 bg-amber-500/10 border border-amber-500/25 rounded-xl px-3 py-2">{t("crm.partner.selectResidenceFirst")}</p>
        ) : (
          <>
            <div className="grid sm:grid-cols-2 gap-3">
              <Field label={t("crm.partner.priority")}>
                <Select value={rel.priority} onValueChange={(v) => setRel({ ...rel, priority: v })} disabled={readOnly}>
                  <SelectTrigger data-testid="partner-r-priority"><SelectValue /></SelectTrigger>
                  <SelectContent>{Object.keys(PARTNER_PRIORITIES).map((k) => <SelectItem key={k} value={k}>{pl(lang, PARTNER_PRIORITIES[k])}</SelectItem>)}</SelectContent>
                </Select>
              </Field>
              <Field label={t("crm.partner.status")}>
                <Select value={rel.status} onValueChange={(v) => setRel({ ...rel, status: v })} disabled={readOnly}>
                  <SelectTrigger data-testid="partner-r-status"><SelectValue /></SelectTrigger>
                  <SelectContent>{PARTNER_STATUSES.map((s) => <SelectItem key={s.k} value={s.k}>{pl(lang, s)}</SelectItem>)}</SelectContent>
                </Select>
              </Field>
              <Field label={t("crm.partner.responsible")}><Input value={rel.responsible} onChange={(e) => setRel({ ...rel, responsible: e.target.value })} disabled={readOnly} data-testid="partner-r-responsible" /></Field>
              <div className="sm:col-span-2"><Field label={t("crm.partner.localNotes")}><Textarea rows={2} value={rel.notes} onChange={(e) => setRel({ ...rel, notes: e.target.value })} disabled={readOnly} /></Field></div>
            </div>
            {!readOnly && <Button onClick={saveRelation} variant="outline" className="w-full sm:w-auto" data-testid="partner-save-relation"><Save className="h-4 w-4 mr-2" /> {t("crm.actions.save")} — {t("crm.partner.sectionRelation")}</Button>}
          </>
        )}

        {/* RENDEZ-VOUS */}
        {relationEditable && (
          <>
            <div className="flex items-center justify-between pt-2">
              <SectionTitle>{t("crm.meeting.section")}</SectionTitle>
              {!readOnly && <Button size="sm" onClick={() => { setEditMeeting(null); setMeetingOpen(true); }} className="bg-purple-600 hover:bg-purple-500" data-testid="partner-add-meeting"><Plus className="h-4 w-4 mr-1" /> {t("crm.meeting.add")}</Button>}
            </div>
            <div className="space-y-2" data-testid="partner-meetings-list">
              {meetings.map((m) => (
                <div key={m.id} className="rounded-xl border border-white/10 bg-white/[0.02] p-3 text-sm flex items-start justify-between gap-2 cursor-pointer hover:border-purple-400/30" onClick={() => { setEditMeeting(m); setMeetingOpen(true); }} data-testid={`partner-meeting-${m.id}`}>
                  <div className="min-w-0">
                    <div className="font-semibold text-foreground/90 flex items-center gap-2 flex-wrap">
                      <CalendarCheck className="h-4 w-4 text-purple-400 shrink-0" /> {m.title || t("crm.meeting.section")}
                      <span className="rounded-full px-2 py-0.5 text-[10px] font-semibold" style={{ color: MTG_COLOR[m.status], background: MTG_COLOR[m.status] + "22" }}>{t(`crm.meeting.st_${m.status}`)}</span>
                      {m.status === "realise" && !reportOk(m) && <span className="rounded-full px-2 py-0.5 text-[10px] font-semibold bg-rose-500/20 text-rose-300" data-testid={`meeting-report-badge-${m.id}`}>{t("crm.meeting.reportBadge")}</span>}
                    </div>
                    <div className="text-xs text-muted-foreground mt-0.5">{(m.date || "").replace("T", " ").slice(0, 16)}</div>
                  </div>
                  {!readOnly && <button onClick={(e) => { e.stopPropagation(); delMeeting(m); }} className="h-7 w-7 rounded-lg flex items-center justify-center text-rose-400 hover:bg-white/5 shrink-0" data-testid={`partner-meeting-delete-${m.id}`}><Trash2 className="h-3.5 w-3.5" /></button>}
                </div>
              ))}
              {!meetings.length && <p className="text-muted-foreground text-sm py-3 text-center">{t("crm.meeting.noMeetings")}</p>}
            </div>
          </>
        )}

        {/* INTERACTIONS */}
        <div className="flex items-center justify-between pt-2">
          <SectionTitle>{t("crm.partner.sectionInteractions")}</SectionTitle>
          {!readOnly && relationEditable && !showAdd && <Button size="sm" onClick={() => setShowAdd(true)} className="bg-fuchsia-600 hover:bg-fuchsia-500" data-testid="partner-add-interaction"><Plus className="h-4 w-4 mr-1" /> {t("crm.partner.addInteraction")}</Button>}
        </div>

        {showAdd && (
          <div className="rounded-xl border border-fuchsia-400/25 bg-fuchsia-500/[0.04] p-3 space-y-3" data-testid="partner-interaction-form">
            <div className="grid sm:grid-cols-2 gap-3">
              <Field label={t("crm.partner.interactionType")}>
                <Select value={ni.type} onValueChange={(v) => setNi({ ...ni, type: v })}>
                  <SelectTrigger data-testid="partner-i-type"><SelectValue /></SelectTrigger>
                  <SelectContent>{PARTNER_INTERACTION_TYPES.map((s) => <SelectItem key={s.k} value={s.k}>{pl(lang, s)}</SelectItem>)}</SelectContent>
                </Select>
              </Field>
              <Field label={t("crm.partner.interactionDate")}><Input type="datetime-local" value={ni.date} onChange={(e) => setNi({ ...ni, date: e.target.value })} data-testid="partner-i-date" /></Field>
              <div className="sm:col-span-2"><Field label={t("crm.partner.interactionNote")}><Textarea rows={2} value={ni.note} onChange={(e) => setNi({ ...ni, note: e.target.value })} data-testid="partner-i-note" /></Field></div>
              <Field label={t("crm.partner.interactionResult")}><Input value={ni.result} onChange={(e) => setNi({ ...ni, result: e.target.value })} /></Field>
              <Field label={t("crm.partner.documents")}><Input value={ni.documents} onChange={(e) => setNi({ ...ni, documents: e.target.value })} placeholder="https://…" /></Field>
            </div>

            <div className="rounded-lg border border-white/10 p-2.5 space-y-2.5">
              <div className="text-xs font-semibold text-foreground/80">{t("crm.partner.nextActionBlock")}</div>
              <div className="flex flex-wrap gap-2">
                <button type="button" onClick={() => setNi({ ...ni, next_mode: "action" })} className="px-3 py-1.5 rounded-lg text-xs font-semibold transition" style={ni.next_mode === "action" ? { background: "rgba(217,70,239,0.18)", color: "#e879f9" } : { color: "rgba(255,255,255,0.5)", border: "1px solid rgba(255,255,255,0.1)" }} data-testid="partner-i-mode-action">{t("crm.partner.createAction")}</button>
                <button type="button" onClick={() => setNi({ ...ni, next_mode: "none" })} className="px-3 py-1.5 rounded-lg text-xs font-semibold transition" style={ni.next_mode === "none" ? { background: "rgba(148,163,184,0.18)", color: "#cbd5e1" } : { color: "rgba(255,255,255,0.5)", border: "1px solid rgba(255,255,255,0.1)" }} data-testid="partner-i-mode-none">{t("crm.partner.noActionNeeded")}</button>
              </div>
              {ni.next_mode === "action" ? (
                <div className="grid sm:grid-cols-3 gap-2">
                  <Field label={t("crm.partner.actionType")}>
                    <Select value={ni.action_type} onValueChange={(v) => setNi({ ...ni, action_type: v })}>
                      <SelectTrigger data-testid="partner-i-action-type"><SelectValue /></SelectTrigger>
                      <SelectContent>{PARTNER_ACTION_TYPES.map((s) => <SelectItem key={s.k} value={s.k}>{pl(lang, s)}</SelectItem>)}</SelectContent>
                    </Select>
                  </Field>
                  <Field label={t("crm.partner.actionResponsible")}><Input value={ni.action_responsible} onChange={(e) => setNi({ ...ni, action_responsible: e.target.value })} /></Field>
                  <Field label={t("crm.partner.relanceDate")}><Input type="date" value={ni.relance_date} onChange={(e) => setNi({ ...ni, relance_date: e.target.value })} data-testid="partner-i-relance-date" /></Field>
                </div>
              ) : (
                <Field label={t("crm.partner.noActionReason")}><Input value={ni.no_action_reason} onChange={(e) => setNi({ ...ni, no_action_reason: e.target.value })} data-testid="partner-i-no-reason" /></Field>
              )}
            </div>

            <div className="flex justify-end gap-2">
              <Button variant="outline" size="sm" onClick={() => setShowAdd(false)}>{t("crm.actions.cancel")}</Button>
              <Button size="sm" onClick={addInteraction} className="bg-fuchsia-600 hover:bg-fuchsia-500" data-testid="partner-save-interaction">{t("crm.partner.saveInteraction")}</Button>
            </div>
          </div>
        )}

        <div className="space-y-2" data-testid="partner-interactions-list">
          {interactions.map((it) => (
            <div key={it.id} className="rounded-xl border border-white/10 bg-white/[0.02] p-3 text-sm">
              <div className="flex items-center justify-between gap-2 flex-wrap">
                <span className="font-semibold text-foreground/90">{pl(lang, interactionEntry(it.type)) || it.type}</span>
                <span className="text-xs text-muted-foreground">{(it.date || "").replace("T", " ").slice(0, 16)} · {it.author}</span>
              </div>
              {it.note && <p className="text-foreground/70 mt-1 whitespace-pre-wrap">{it.note}</p>}
              {it.result && <p className="text-xs text-emerald-300/80 mt-1">→ {it.result}</p>}
              {it.next_mode === "action" && it.relance_date && <p className="text-xs text-amber-300/80 mt-1">↳ {t("crm.partner.nextAction")}: {(actionEntry(it.action_type) ? pl(lang, actionEntry(it.action_type)) : it.action_type)} ({it.relance_date})</p>}
              {it.next_mode === "none" && it.no_action_reason && <p className="text-xs text-muted-foreground mt-1">↳ {t("crm.partner.noActionNeeded")}: {it.no_action_reason}</p>}
            </div>
          ))}
          {!interactions.length && <p className="text-muted-foreground text-sm py-4 text-center">{t("crm.partner.noInteractions")}</p>}
        </div>

        {meetingOpen && <PartnerMeetingDialog partnerId={partnerId} meeting={editMeeting} open={meetingOpen} onClose={() => setMeetingOpen(false)} onSaved={() => { onChanged?.(); load(); }} />}
      </DialogContent>
    </Dialog>
  );
}
